import { useEffect, useState } from "react";
import getAudienceSelectorFromLink from "components/utility/api/GetAudienceSelectorFromLink";
import AudienceSortModal from "./AudienceSortModal";
import { audienceSortStepNames } from "components/public-site/audience-sort/utility/audienceSortStepNames";
import CountrySelector from "components/public-site/audience-sort/CountrySelector";
import CountrySelectorMobile from "components/public-site/audience-sort/CountrySelectorMobile";
import ProfessionalAffirmation from "components/public-site/audience-sort/ProfessionalAffirmation";
import ProfessionalAffirmationMobile from "components/public-site/audience-sort/ProfessionalAffirmationMobile";
import {
	AudienceSelectionProps,
	AudienceSplashModalSelectorData,
	CountryListDataProps
} from "components/public-site/audience-sort/utility/audienceSortPropTypes";
import { SCREEN_WIDTHS } from "components/utility/constants/screen_widths";
import AudienceSelectorFromLinkEntry from "./AudienceSelectorFromLinkEntry";

type ModalEntryPoint = {
	countryKey: string;
	audienceKey: string;
	redirectUrl: string;
	apiUrl: string;
};

const AudienceSelectorFromLink = () => {
	const [initialData, setInitialData] = useState<AudienceSplashModalSelectorData | null>(null);
	const [step, setStep] = useState(audienceSortStepNames.initialStep);
	const [splashModalAudienceTypes, setSplashModalAudienceTypes] =
		useState<AudienceSplashModalSelectorData | null>(null);
	const [audienceSelectionData, setAudienceSelectionData] = useState<AudienceSelectionProps | null>(
		null
	);
	const [countryListData, setCountryListData] = useState<CountryListDataProps | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(true);
	const [cameFromInitialStep, setCameFromInitialStep] = useState(true);
	const [modalJustOpened, setModalJustOpened] = useState<boolean>(true);
	const [isMobile, setIsMobile] = useState(window.innerWidth <= SCREEN_WIDTHS.SM);
	const [isMobileXS, setIsMobileXS] = useState(window.innerWidth <= SCREEN_WIDTHS.XS);

	const currentPage = "from-link";
	const modalColorTheme = "light-theme-teal";

	useEffect(() => {
		if (isModalOpen) {
			const data: ModalEntryPoint = window["audienceSelectorModalFromLink"];
			const countryKey = data.countryKey ? data.countryKey : "";
			const apiUrl = data.apiUrl;
			getAudienceSelectorFromLink(countryKey, apiUrl).then((apiResponse) => {
				if (apiResponse.status === "success" && typeof apiResponse.data !== "string") {
					setInitialData(apiResponse.data);
					setSplashModalAudienceTypes(apiResponse.data);
				}
			});
		}
	}, [isModalOpen]);

	useEffect(() => {
		const isMobileFunc = () => {
			setIsMobile(window.innerWidth <= SCREEN_WIDTHS.SM);
			setIsMobileXS(window.innerWidth <= SCREEN_WIDTHS.XS);
		};
		window.addEventListener("resize", isMobileFunc);
		return () => {
			window.removeEventListener("resize", isMobileFunc);
		};
	}, []);

	return (
		<>
			{initialData && Object.keys(initialData).length ? (
				<AudienceSortModal
					theme={modalColorTheme}
					currentPage={currentPage}
					currentStep={step}
					setStep={setStep}
					isMobile={false}
					isModalOpen={isModalOpen}
					isWide={
						!(
							step === audienceSortStepNames.initialStep ||
							step === audienceSortStepNames.audienceTypeSelection
						)
					}
					className="audience-selector-from-link-modal"
					modalJustOpened={modalJustOpened}
					preventHandleClickOutside={true}
				>
					{step === audienceSortStepNames.initialStep && (
						<AudienceSelectorFromLinkEntry
							audienceTypesData={initialData}
							currentPage={currentPage}
							setCountryListData={setCountryListData}
							setAudienceSelectionData={setAudienceSelectionData}
							setIsModalOpen={setIsModalOpen}
							setModalJustOpened={setModalJustOpened}
							setStep={setStep}
						/>
					)}
					{step === audienceSortStepNames.financialProfessionalAffirmation &&
						!isMobileXS &&
						audienceSelectionData &&
						Object.keys(audienceSelectionData).length && (
							<ProfessionalAffirmation
								hasCloseButton={false}
								audienceSortTheme={modalColorTheme}
								audienceSelectionData={audienceSelectionData}
								setStep={setStep}
								cameFromInitialStep={cameFromInitialStep}
								setIsModalOpen={setIsModalOpen}
								currentPage={currentPage}
							/>
						)}
					{step === audienceSortStepNames.financialProfessionalAffirmation &&
						isMobileXS &&
						audienceSelectionData &&
						Object.keys(audienceSelectionData).length && (
							<ProfessionalAffirmationMobile
								audienceSelectionData={audienceSelectionData}
								currentPage={currentPage}
								hasHeader={true}
								hasCloseButton={false}
								setStep={setStep}
								cameFromInitialStep={cameFromInitialStep}
								audienceSortTheme={modalColorTheme}
							/>
						)}
					{step === audienceSortStepNames.countrySelection &&
						!isMobile &&
						countryListData &&
						Object.keys(countryListData).length && (
							<CountrySelector
								cameFromInitialStep={cameFromInitialStep}
								currentPage={currentPage}
								setStep={setStep}
								hasCloseButton={false}
								isMobile={isMobile}
								countryListData={countryListData}
								setSplashModalAudienceTypes={setSplashModalAudienceTypes}
								setCameFromInitialStep={setCameFromInitialStep}
								setIsModalOpen={setIsModalOpen}
								setModalJustOpened={setModalJustOpened}
								audienceSortTheme={modalColorTheme}
							/>
						)}
					{step === audienceSortStepNames.countrySelection &&
						isMobile &&
						countryListData &&
						Object.keys(countryListData).length && (
							<CountrySelectorMobile
								cameFromInitialStep={cameFromInitialStep}
								currentPage={currentPage}
								hasCloseButton={false}
								setStep={setStep}
								countryListData={countryListData}
								setSplashModalAudienceTypes={setSplashModalAudienceTypes}
								setCameFromInitialStep={setCameFromInitialStep}
								audienceSortTheme={modalColorTheme}
							/>
						)}
					{step === audienceSortStepNames.audienceTypeSelection &&
						splashModalAudienceTypes &&
						Object.keys(splashModalAudienceTypes).length && (
							<AudienceSelectorFromLinkEntry
								audienceTypesData={splashModalAudienceTypes}
								currentPage={currentPage}
								setStep={setStep}
								setAudienceSelectionData={setAudienceSelectionData}
								setIsModalOpen={setIsModalOpen}
								setModalJustOpened={setModalJustOpened}
								setCountryListData={setCountryListData}
							/>
						)}
				</AudienceSortModal>
			) : null}
		</>
	);
};

export default AudienceSelectorFromLink;
